import type { FlightCardData } from '@massilia/queries'

import { Button, FullScreenOverlay } from '@massilia/ui'
import { RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'

import { RESET_FILTERS_PATCH } from './filter-url-params'
import {
  FiltersStateContext,
  type FiltersStateContextValue,
  type FlightFilterParams,
  useFiltersState,
} from './filters-state-context'
import { FlightFilters } from './flight-filters'
import { useHasActiveFilters } from './use-has-active-filters'

interface FiltersModalFooterProps {
  offers: FlightCardData[]
  onApply: () => void
  onReset: () => void
}

interface FiltersModalProps {
  isOpen: boolean
  offers: FlightCardData[]
  onClose: () => void
}

/**
 * Filtres en plein écran sur mobile. Les modifications sont un BROUILLON : elles
 * ne partent dans l'URL qu'au clic sur « Appliquer », ce qui évite de relancer
 * le rendu de la liste à chaque case cochée.
 *
 * Ce brouillon doit être branché sur le contexte, sinon les composants de filtre
 * écrivent directement dans l'URL et « Appliquer » leur réécrit par-dessus
 * l'instantané pris à l'ouverture — c'est ce qui faisait disparaître les filtres
 * bagage, paiement et escales (les trois qui ont un `.default()`, donc toujours
 * présents dans l'instantané) à la fermeture de la modale.
 */
export function FiltersModal({ isOpen, offers, onClose }: FiltersModalProps) {
  const { filters, setFilters } = useFiltersState()
  const [localFilters, setLocalFilters] = useState(() => withNeutralDefaults(filters))

  // Réinitialise le brouillon sur les filtres actifs à chaque ouverture — pas
  // d'effect : on ajuste l'état pendant le rendu (cf. doc React) pour éviter un
  // rendu « périmé » avant que l'effect ne s'exécute.
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) {
      setLocalFilters(withNeutralDefaults(filters))
    }
  }

  const draftState = useMemo<FiltersStateContextValue>(
    () => ({
      filters: localFilters,
      setFilters: (partialFilters) => {
        setLocalFilters((prev) => withNeutralDefaults({ ...prev, ...partialFilters }))
      },
    }),
    [localFilters]
  )

  // Le pied de page est rendu par l'overlay À L'INTÉRIEUR de cet arbre React (un
  // portail conserve le contexte) : « Réinitialiser » écrit donc lui aussi dans
  // le brouillon.
  const footer = (
    <FiltersModalFooter
      offers={offers}
      onApply={() => {
        setFilters(localFilters)
        onClose()
      }}
      // « Réinitialiser » vaut validation : il écrit dans l'URL et referme, sans
      // demander un second passage par « Appliquer ».
      onReset={() => {
        setFilters(RESET_FILTERS_PATCH)
        onClose()
      }}
    />
  )

  return (
    <FiltersStateContext.Provider value={draftState}>
      <FullScreenOverlay
        footer={footer}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        open={isOpen}
        title="Filtres"
      >
        <div className="px-4 py-4">
          <FlightFilters offers={offers} variant="plain" />
        </div>
      </FullScreenOverlay>
    </FiltersStateContext.Provider>
  )
}

function FiltersModalFooter({ offers, onApply, onReset }: FiltersModalFooterProps) {
  // Lu sur le BROUILLON : le bouton se grise dès que l'utilisateur a tout remis
  // à zéro à la main, sans attendre l'écriture dans l'URL.
  const hasActiveFilters = useHasActiveFilters(offers)

  return (
    <div className="flex flex-col gap-2">
      <Button className="w-full" disabled={!hasActiveFilters} onClick={onReset} variant="outline">
        <RotateCcw className="size-4" />
        <span className="text-sm font-medium text-gray-900">Réinitialiser les filtres</span>
      </Button>
      <Button className="w-full bg-brand-600 hover:bg-brand-700" onClick={onApply}>
        Appliquer
      </Button>
    </div>
  )
}

/**
 * Valeurs neutres des filtres qui ont un `.default()` dans le schéma de search.
 * Le brouillon vit hors de l'URL, donc rien ne les y réinjecte : sans ce
 * garde-fou, « Réinitialiser » laissait ces trois champs à `undefined` et les
 * `RadioGroup` passaient de contrôlés à non contrôlés en plein rendu.
 */
function withNeutralDefaults(filters: FlightFilterParams): FlightFilterParams {
  return {
    ...filters,
    filterLuggage: filters.filterLuggage ?? '0',
    filterPayment: filters.filterPayment ?? 'all',
    filterStops: filters.filterStops ?? 'all',
  }
}
